import { io, Socket } from 'socket.io-client';

class SocketService {
    private socket: Socket | null = null;
    private isConnected = false;
    private listeners: Map<string, Set<(data: any) => void>> = new Map();

    constructor() { }

    init(userId: string, backendUrl: string = 'http://localhost:3000') {
        if (this.socket) return;

        console.log('Initializing SocketService for user:', userId);

        this.socket = io(`${backendUrl}/call`, {
            transports: ['websocket'],
            autoConnect: true,
        });

        this.socket.on('connect', () => {
            console.log('Socket connected:', this.socket?.id);
            this.isConnected = true;
            this.joinRoom(userId);
        });

        this.socket.on('disconnect', () => {
            console.log('Socket disconnected');
            this.isConnected = false;
        });

        this.socket.on('connect_error', (err) => {
            console.error('Socket connection error:', err);
        });

        // Listen for all events and dispatch to registered listeners
        this.socket.onAny((eventName, ...args) => {
            const callbacks = this.listeners.get(eventName);
            if (callbacks) {
                callbacks.forEach(cb => cb(args[0]));
            }
        });

        // Handle standard call events
        this.registerInternalListeners();
    }

    private registerInternalListeners() {
        if (!this.socket) return;

        this.socket.on('call.starting', (data) => {
            console.log('Call starting event:', data);
            window.dispatchEvent(new CustomEvent('BackendCallStarting', { detail: data }));
        });

        this.socket.on('call.ended', (data) => {
            console.log('Call ended event:', data);
            window.dispatchEvent(new CustomEvent('BackendCallEnded', { detail: data }));
        });
    }

    joinRoom(userId: string) {
        if (this.socket && this.isConnected) {
            this.socket.emit('joinRoom', { userId });
        } else {
            // Retry after connection
            this.socket?.once('connect', () => {
                this.socket?.emit('joinRoom', { userId });
            });
        }
    }

    leaveRoom(userId: string) {
        if (this.socket && this.isConnected) {
            this.socket.emit('leaveRoom', { userId });
        }
    }

    on(event: string, callback: (data: any) => void) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(callback);

        // Also listen on the socket if we haven't already for this specific event? 
        // onAny handles it, so no need to register individual socket listeners unless needed.
    }

    off(event: string, callback: (data: any) => void) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.delete(callback);
        }
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.isConnected = false;
        }
    }
}

export const socketService = new SocketService();
